import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  alpha,
  useTheme,
  Stack,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useQuery, useQueryClient } from "react-query";
import {
  createCandidateEmployment,
  getCandidateEmployment,
  updateCandidateEmployment,
} from "../../../api/services/candidate";
import CandidateLookingFor from "../../../components/CandidateForm/CandidatesLookingFor";
import CandidateRoleForm from "../../../components/CandidateForm/CandidateRoleForm";
import CandidatePayment from "../../../components/CandidateForm/CandidatePayment";
import { QK } from "../../../base/qk";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import {
  Work as WorkIcon,
  Save as SaveIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";

const CandidatesEmployment = ({ clickedId }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({ candidate: clickedId });
  const [isLoading, setIsLoading] = useState(false);
  const [employmentId, setEmploymentId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data } = useQuery(
    [QK.candidateEmployment, clickedId],
    () => getCandidateEmployment(clickedId),
    {
      enabled: !!clickedId,
      onSuccess: (data) => {
        setFormData(data);
        setEmploymentId(data.id);
      },
    }
  );

  useEffect(() => {
    if (data) {
      setFormData(data);
      setEmploymentId(data.id);
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      candidate: clickedId,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleOpenConfirm = (e) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    setIsLoading(true);
    try {
      if (employmentId) {
        const response = await updateCandidateEmployment(employmentId, formData);
        if (response.status === 200) {
          enqueueSnackbar("Changes saved successfully", { variant: "success" });
          queryClient.invalidateQueries([QK.candidateEmployment, clickedId]);
        }
      } else {
        const response = await createCandidateEmployment(formData);
        if (response.status === 201) {
          enqueueSnackbar("Employment created successfully", { variant: "success" });
          queryClient.invalidateQueries([QK.candidateEmployment, clickedId]);
        }
      }
    } catch (error) {
      enqueueSnackbar("Error processing request", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 0, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
          pb: 2,
          borderBottom: "1px solid #E2E8F0",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WorkIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#1A1A2E">
              Employment & Payroll
            </Typography>
            <Typography variant="caption" color="#6B7280">
              Manage candidate payment rates, roles, and job preferences
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MoneyIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
          <Typography variant="caption" color="#6B7280">
            {employmentId ? "Updating existing" : "Creating new profile"}
          </Typography>
        </Box>
      </Box>

      <Box component="form" onSubmit={handleOpenConfirm} noValidate>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          <Box sx={{ flex: "1 1 58%", minWidth: "350px" }}>
            <CandidatePayment handleChange={handleChange} formData={formData} />
          </Box>

          <Box sx={{ flex: "1 1 35%", minWidth: "300px" }}>
            <Stack spacing={2.5}>
              <CandidateRoleForm handleChange={handleChange} formData={formData} />
              <CandidateLookingFor handleChange={handleChange} formData={formData} />
            </Stack>
          </Box>
        </Box>

        {/* Footer Action Bar */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 10,
            mt: 3,
            py: 1.5,
            px: 2.5,
            bgcolor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(12px)",
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? null : <SaveIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              bgcolor: "#2469F0",
              px: 3.5,
              py: 1,
              fontSize: "0.85rem",
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {isLoading ? "Saving..." : "Update Details"}
          </Button>
        </Box>
      </Box>

      <ConfirmModal
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleConfirm={handleSubmit}
        title="Update Employment Data"
        body="This will update the candidate's payroll and role details. Proceed?"
        isLoading={isLoading}
      />
    </Container>
  );
};

export default CandidatesEmployment;